import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, getAutoSlip, CalAm } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { fnGetIoFgTrns } from "@shared/utils/DrilldownUtils";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, ExecuteRunner } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnCodeBox from "@shared/components/SnCodeBox";
import SnLabel from "@shared/components/SnLabel";
import SnDateBox from "@shared/components/SnDateBox";
import SnButton from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

interface CodeValue {
  code: string;
  name: string;
}

interface TabState {
  page: string;
  searchParams: any | null;
}

const SN_MTL_ISU_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);

  const isFirstRender = useRef(true); // 화면 오픈시 조회 방지

  // 처리 후 '처리' 탭으로 이동할 때 조회조건의 출하일을 오늘로 덮어쓰므로,
  // 덮어쓰기 직전의 '미처리' 조회조건을 보관했다가 미처리 탭으로 돌아올 때 되돌린다.
  const prevSearchParams = useRef<any>(null);

  const { userInfo, config } = global(); // Login user 정보
  //#endregion

  // 처리 컴포넌트
  const [dateBox, setDateBoxValue] = useState<string>(dateToString(new Date()));
  const [codeWh, setWhCode] = useState<CodeValue>({ code: "", name: "" });
  const [codeEmp, setEmpCode] = useState<CodeValue>({
    code: userInfo.cd_emp || "",
    name: userInfo.nm_emp || "",
  });

  const [tabPageState, setTabState] = useState<TabState>({
    page: "1",
    searchParams: null,
  });

  const dateValueChanged = async (value: string) => {
    setDateBoxValue(value);
  };

  const whValueChanged = async (code: string, name: string, rowData?: any) => {
    setWhCode({ code: code, name: name });
  };

  const empValueChanged = async (code: string, name: string, rowData?: any) => {
    setEmpCode({ code: code, name: name });
  };

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (tabPageState.page) {
      if (!tabPageState.searchParams) {
        onSearch(null);
      } else {
        onSearch(tabPageState.searchParams);
      }
    }
  }, [tabPageState.page]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onExecute,
    onPrint,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      if (tabPageState.page === "1") {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_isu_reg_001_01",
          params: searchDataToUse,
        };

        resetCondition();

        getGridCurrent("grid1").resetUpdate();
        getGridCurrent("grid2").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: data[0],
          grid2: data[1] || [],
          grid3: [],
          grid4: [],
        });
      } else {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_isu_reg_001_02",
          params: searchDataToUse,
        };

        getGridCurrent("grid3").resetUpdate();
        getGridCurrent("grid4").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: [],
          grid3: data[0],
          grid4: data[1] || [],
        });
      }
    } catch (error: any) {}
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    let hasInvalid = false;

    try {
      switch (id) {
        case "Save": // 처리
          {
            const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            if (dateBox === "") {
              await showDialog("alter", "출하일을 입력해 주세요.");
              return false;
            }

            if (codeEmp.code === "") {
              await showDialog("alter", "담당자를 입력해 주세요.");
              return false;
            }

            let hasInvalidcdItem = "";

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              if (node.data.qt !== node.data.qt_isu) {
                if (!hasInvalid) {
                  hasInvalid = true;
                  hasInvalidcdItem = node.data.cd_item;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "품목코드 [" +
                  hasInvalidcdItem +
                  "]의 의뢰수량과 출하수량이 같지 않습니다."
              );
              return false;
            }

            const qtioH: any[] = [];
            const qtio: any[] = [];

            const insertDataH = {
              cd_firm: userInfo.cd_firm,
              no_io: "",
              cd_biz: focusedRowNodeH.data.cd_biz,
              cd_wh: codeWh.code ? codeWh.code : "",
              dt_io: dateBox,
              cd_cust: focusedRowNodeH.data.cd_cust,
              cd_cust_dlv: focusedRowNodeH.data.cd_cust_dlv,
              cd_exch: focusedRowNodeH.data.cd_exch,
              rt_exch: focusedRowNodeH.data.rt_exch,
              cd_io: focusedRowNodeH.data.cd_io,
              fg_slip: "2",
              tp_slip: "2",
              fg_io: focusedRowNodeH.data.fg_io,
              fg_money: focusedRowNodeH.data.fg_money,
              fg_trns: focusedRowNodeH.data.fg_trns,
              // cd_dept: focusedRowNodeH.data.cd_dept,
              cd_emp: codeEmp.code,
              // cd_gp: focusedRowNodeH.data.cd_gp,
              tp_trans: focusedRowNodeH.data.tp_trans,
              cd_pjt: focusedRowNodeH.data.cd_pjt,
              cd_mgmtdtl: focusedRowNodeH.data.cd_mgmtdtl,
              dc_rmk: focusedRowNodeH.data.dc_rmk,
              am: focusedRowNodeH.data.am,
              am_k: focusedRowNodeH.data.am_k,
              am_vat: focusedRowNodeH.data.am_vat,
              am_supply: focusedRowNodeH.data.am_supply,
            };

            qtioH.push(insertDataH);

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              const insertDataL = {
                cd_firm: userInfo.cd_firm,
                no_io: "",
                no_line: node.data.no_line,
                cd_biz: focusedRowNodeH.data.cd_biz,
                cd_wh: codeWh.code ? codeWh.code : node.data.cd_wh,
                cd_io: focusedRowNodeH.data.cd_io,
                fg_slip: "2",
                tp_slip: "2",
                fg_io: focusedRowNodeH.data.fg_io,
                fg_money: focusedRowNodeH.data.fg_money,
                cd_item: node.data.cd_item,
                qt_good: node.data.qt_good,
                qt_bad: node.data.qt_bad,
                qt: node.data.qt,
                um: node.data.um,
                am: node.data.am,
                rt_exch: node.data.rt_exch,
                um_k: node.data.um_k,
                am_k: node.data.am_k,
                am_vat: node.data.am_vat,
                fg_vat: node.data.fg_vat,
                um_supply: node.data.um_supply,
                am_supply: node.data.am_supply,
                dt_rtn_plan: node.data.dt_rtn_plan,
                no_order: node.data.no_so,
                no_line_order: node.data.no_line_so,
                no_req: node.data.no_req,
                no_line_req: node.data.no_line,
                no_lc: node.data.no_lc,
                no_line_lc: node.data.no_line_lc,
                dc_rmk: node.data.dc_rmk,
                dt_io: dateBox,
                cd_pjt: focusedRowNodeH.data.cd_pjt,
              };
              qtio.push(insertDataL);
            });

            const cd_module: any = Menuprops.cd_module;
            const no_slip = await getAutoSlip(
              cd_module,
              8,
              dateBox,
              Menuprops.cd_menu
            );

            qtioH[0].no_io = no_slip;

            for (let row of qtio) {
              row.no_io = no_slip;
            }

            // 요청 데이터 구성
            const SaveData = {
              qtioh: { insert: qtioH },
              qtio: { insert: qtio },
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_ISU_REG_001_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid1").resetUpdate();
            getGridCurrent("grid2").resetUpdate();
            resetCondition();

            const mtl0011 = config?.MTL0011?.dc_ctrl ?? "";

            switch (mtl0011) {
              case "Y": {
                // 처리로 이동
                const currentSearch =
                  searchRef.current?.getFormdata() ?? formData;
                // 출하일로 덮어쓰기 전 조회조건 보관 (미처리 탭 복귀시 복원)
                prevSearchParams.current = { ...currentSearch };

                const isuDate = {
                  ...currentSearch,
                  dt_from: dateBox,
                  dt_to: dateBox,
                };
                setFormData(isuDate);

                setTabState({ page: "2", searchParams: isuDate });
                break;
              }
              default: // 재조회
                onSearch(null);
                break;
            }
          }
          break;
        case "Delete": // 취소
          {
            const focusedRowNodeH = getGridCurrent("grid3").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            getGridCurrent("grid4").forEachFilteredNode((node: RowNode) => {
              if (node.data.cls > 0) {
                if (!hasInvalid) {
                  hasInvalid = true;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "마감 처리된 건이 존재하여 취소 할 수 없습니다"
              );
              return false;
            }

            const confirm = await showDialog("confirm", "취소하시겠습니까?");

            if (!confirm) {
              return; // 사용자가 아니오를 선택하면 함수 종료
            }

            getGridCurrent("grid4").deleteAllRows();
            getGridCurrent("grid3").deleteSelectedRows();

            const qtiohChanges = getGridCurrent("grid3").getChange();
            const qtioChanges = getGridCurrent("grid4").getChange();

            // 요청 데이터 구성
            const SaveData = {
              qtioh: qtiohChanges,
              qtio: qtioChanges,
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_ISU_REG_001_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid3").resetUpdate();
            getGridCurrent("grid4").resetUpdate();

            setTabState({
              page: "1",
              searchParams: restorePrevSearchParams(),
            });
          }
          break;
      }
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
    }
  };
  //#endregion

  //#region onPrint
  const onPrint = async () => {
    switch (tabPageState.page) {
      case "1": {
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH) {
          return {
            cd_firm: focusedRowNodeH.data.cd_firm,
            cd_biz: focusedRowNodeH.data.cd_biz,
            no_req: focusedRowNodeH.data.no_req,
          };
        } else {
          return;
        }
      }
      case "2": {
        const focusedRowNodeH = getGridCurrent("grid5").focusedRowNode();
        if (focusedRowNodeH) {
          const focusedRowNodeL = getGridCurrent("grid6").focusedRowNode();
          if (focusedRowNodeL) {
            return {
              cd_firm: focusedRowNodeH.data.cd_firm,
              cd_biz: focusedRowNodeH.data.cd_biz,
              no_req: focusedRowNodeL.data.no_req,
            };
          } else {
            return;
          }
        } else {
          return;
        }
      }
    }
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      // 거래구분이 조회 필수라 수불번호의 거래구분을 같이 넣는다(없으면 기본값 국내로 조회돼 0건).
      const fg_trns = await fnGetIoFgTrns(nodeData.no_io);
      const selectedData = {
        ...(fg_trns ? { fg_trns } : {}),
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      // 이미 "처리" 탭이면 page 가 그대로라 [tabPageState.page] effect 가 안 돈다 → 여기서 바로 재조회
      if (tabPageState.page === "2") onSearch(searchData);
      setTabState({ page: "2", searchParams: searchData });
    }
  };
  //#endregion

  //#region GridL Events
  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_good":
        rowNode.setDataValue("qt_bad", rowData.qt - rowData.qt_good, "noEvent");
        rowNode.setDataValue(
          "qt_isu",
          rowData.qt_good + rowData.qt_bad,
          "noEvent"
        );
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], "rt_exch");
        break;
      case "qt_bad":
        rowNode.setDataValue("qt_good", rowData.qt - rowData.qt_bad, "noEvent");
        rowNode.setDataValue(
          "qt_isu",
          rowData.qt_good + rowData.qt_bad,
          "noEvent"
        );
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], "rt_exch");
        break;
    }
  };

  //#region GridH2 Events
  const onFocusedRowChangedH2 = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid4").selectAndFocusRow(0);
      setSelectedDocu(no_io);
    } else {
      return;
    }
  };
  //#endregion

  //#region onTabChanged
  // 처리 이동으로 덮어썼던 조회조건을 원래대로 되돌린다. 보관된 값이 없으면 null.
  const restorePrevSearchParams = (): any => {
    const saved = prevSearchParams.current;
    if (!saved) return null;

    prevSearchParams.current = null;
    setFormData(saved);
    return saved;
  };

  const onTabChanged = async (index: string) => {
    if (index === "1") {
      setTabState({ page: index, searchParams: restorePrevSearchParams() });
      return;
    }
    setTabState({ page: index, searchParams: null });
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      return;
    }

    switch (id) {
      case "Good":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (node.data.yn_qc !== "Y") {
            const qt = node.data.qt;
            node.setDataValue("qt_bad", 0, "noEvent");
            node.setDataValue("qt_good", qt, "noEvent");
            node.setDataValue("qt_isu", qt, "noEvent");
          }
        });
        break;
      case "Bad":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (node.data.yn_qc !== "Y") {
            const qt = node.data.qt;
            node.setDataValue("qt_good", 0, "noEvent");
            node.setDataValue("qt_bad", qt, "noEvent");
            node.setDataValue("qt_isu", qt, "noEvent");
          }
        });
        break;
      case "Qc":
        let hasInvalid = false;

        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (node.data.yn_qc === "Y" && node.data?.st_qc !== "C") {
            hasInvalid = true;
          }

          if (
            node.data.yn_qc === "Y" &&
            node.data.st_qc === "C" &&
            node.data.qt === node.data.qt_qc
          ) {
            switch (node.data.yn_confirm) {
              case "Y": // 합격
                node.setDataValue(
                  "qt_good",
                  node.data.qt_qc - node.data.qt_qc_bad,
                  "noEvent"
                );
                node.setDataValue("qt_bad", node.data.qt_qc_bad, "noEvent");
                node.setDataValue("qt_isu", node.data.qt_qc, "noEvent");
                break;
              case "N": // 불합격
                node.setDataValue("qt_bad", node.data.qt_qc, "noEvent");
                node.setDataValue("qt_isu", node.data.qt_qc, "noEvent");
                break;
              case "R": // 특채
                node.setDataValue("qt_good", node.data.qt_qc_rcl, "noEvent");
                node.setDataValue("qt_bad", node.data.qt_qc_bad, "noEvent");
                node.setDataValue("qt_isu", node.data.qt_qc, "noEvent");
                break;
            }
          }
        });

        if (hasInvalid) {
          await showDialog("alter", "검사가 발송되지 않은 건이 존재합니다.");
        }
        break;
    }
  };
  //#endregion

  //#region Method
  const resetCondition = (): any => {
    setWhCode({ code: "", name: "" });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH2,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnTab
          titles={["미처리", "처리"]}
          width={"100%"}
          selectedTab={tabPageState.page}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <div style={{ width: 60 }}>
                    <SnLabel text="출하일" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnDateBox
                      defaultValue={dateBox}
                      onValueChanged={dateValueChanged}
                    />
                  </div>
                  <div style={{ width: 70 }}>
                    <SnLabel text="출고창고" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnCodeBox
                      code="POPUP_WH"
                      onValueChanged={whValueChanged}
                      defaultValue={{
                        code: codeWh.code,
                        name: codeWh.name,
                      }}
                    />
                  </div>
                  <div style={{ width: 60 }}>
                    <SnLabel text="담당자" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnCodeBox
                      code="POPUP_EMP"
                      defaultValue={{
                        code: codeEmp.code,
                        name: codeEmp.name,
                      }}
                      onValueChanged={empValueChanged}
                      isEditable={true}
                    />
                  </div>
                  <div>
                    <SnButton
                      text={"양품적용"}
                      onClick={() => {
                        onSnButtonClick("Good");
                      }}
                    />
                    <SnButton
                      text={"불량적용"}
                      onClick={() => {
                        onSnButtonClick("Bad");
                      }}
                    />
                    <SnButton
                      text={"검사적용"}
                      onClick={() => {
                        onSnButtonClick("Qc");
                      }}
                    />
                  </div>
                  <div style={{ paddingLeft: "80px" }}>
                    <SnButton
                      text={"처리"}
                      onClick={() => {
                        ExecuteRunner.run("Save");
                      }}
                    />
                  </div>
                </div>,
              ]}
            />,
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <SnButton
                    text={"취소"}
                    onClick={() => {
                      ExecuteRunner.run("Delete");
                    }}
                  />
                </div>,
              ]}
            />,
          ]}
          body={[
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid1"]} // Grid Name
                  columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                  rowData={grids.grid1.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  filter={{
                    refs: [gridRefs.current["grid2"]],
                    columns: ["no_req"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel2" orgSize="64.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnGrid
                    ref={gridRefs.current["grid2"]} // Grid Name
                    columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                    rowData={grids.grid2.dataSource} // Datasource
                    etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                    events={grids.grid2.events}
                    filter={{
                      refs: [gridRefs.current["grid4"]],
                      columns: ["no_req", "no_line"],
                    }}
                  />
                </div>
              </SnResizeBar>
            </div>,
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel5" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={grids.grid3.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  events={grids.grid3.events}
                  filter={{
                    refs: [gridRefs.current["grid4"]],
                    columns: ["no_io"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel6" orgSize="55.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnGrid
                    ref={gridRefs.current["grid4"]} // Grid Name
                    columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                    rowData={grids.grid4.dataSource} // Datasource
                    etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                  />
                </div>
              </SnResizeBar>
              <SnFileUploadBox
                selectedDocu={selectedDocu}
                selectedLineDocu={0}
              />
            </div>,
          ]}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_ISU_REG_001;
